import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Grid, Checkbox, FormControlLabel, Button,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Divider, Alert, Tooltip
} from '@mui/material';
import {
    DescriptionOutlined as DocumentIcon,
    Visibility as PreviewIcon,
    VisibilityOff as HidePreviewIcon,
    UploadFile as UploadIcon,
    DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import { getLeaseDocumentsByPropertyId, generateObjectId, getSignedUrl, extractKeyFromUrl, getLeaseStatus } from '../../utils';
import axios from '../../../../../../../../../api/axios';
import './lease-form.scss';
import { Previewer } from '../../../../../../../../../../components/DocumentPreviewer/Previewer';

const FetchAvailableForms = ({ formik }) => {
    const { property_id, unit_no, building_name } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [localSelectedDocuments, setLocalSelectedDocuments] = useState([]);
    const [localCustomDocuments, setLocalCustomDocuments] = useState([]);
    const [previewDocument, setPreviewDocument] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const updateTimeoutRef = useRef(null);
    const hasInitializedRef = useRef(false);
    const lastSyncedDocsRef = useRef(null);

    // Stable tenantIds
    const tenantIds = useMemo(() => {
        return (formik?.values?.tenants || []).map(t => t.id);
    }, [formik?.values?.tenants]);

    // Create tenant signatures
    const createTenantSignatures = useCallback(
        (signatureRequiredFrom) =>
            signatureRequiredFrom.map(tenant => ({
                id: tenant.id,
                name: formik?.values?.tenants?.find(t => t.id === tenant.id)?.name || `Tenant ${tenant.id}`,
                signature: "",
                date: "",
            })),
        [formik?.values?.tenants]
    );

    // Initialize data
    useEffect(() => {
        const initializeData = async () => {
            if (!property_id) {
                setError('Property ID is missing. Cannot fetch documents.');
                setLoading(false);
                return;
            }
            if (tenantIds.length === 0) {
                setError('No tenants found. Please add tenants before configuring documents.');
                setLoading(false);
                return;
            }
            if (hasInitializedRef.current) return;

            try {
                setLoading(true);

                const [propertyResp, leaseResp, docsResp] = await Promise.all([
                    axios.get(`/property/get-property/${property_id}`),
                    getLeaseStatus({ property_id, unit_no, building_name, mode: '' }),
                    getLeaseDocumentsByPropertyId(property_id)
                ]);

                const leaseResponse = leaseResp?.lease || null;
                let processedDocs = [];
                if (docsResp?.documents && Array.isArray(docsResp.documents)) {
                    processedDocs = docsResp.documents.map(doc => ({
                        _id: doc._id,
                        id: doc._id,
                        name: doc.fileName || doc.title || 'Unnamed Document',
                        urls: [{ file_url: doc.url, uploaded_by: 'Default Document', uploaded_at: new Date() }],
                        send_to_users: tenantIds.map(id => ({ id })),
                        signature_required_from: tenantIds.map(id => ({ id })),
                        signatures: createTenantSignatures(tenantIds.map(id => ({ id }))),
                        fileType: doc.fileType || doc.url?.split('.').pop()
                    }));
                    setDocuments(processedDocs);
                }

                const attachedDocs = leaseResponse?.attached_documents || [];
                const isAttachedDocsEmpty = !attachedDocs || attachedDocs.length === 0;

                if (leaseResponse && !isAttachedDocsEmpty) {
                    const apiDocs = [];
                    const customDocs = [];

                    attachedDocs.forEach(doc => {
                        if (doc.urls?.[0]?.file_url === "upload_requested") {
                            customDocs.push({
                                id: doc.id,
                                name: doc.name,
                                included: true,
                                send_to_users: doc.send_to_users || tenantIds.map(id => ({ id })),
                                signature_required_from: doc.signature_required_from || [],
                                signatures: doc.signatures || createTenantSignatures(doc.signature_required_from || [])
                            });
                        } else {
                            const matchedDoc = processedDocs.find(d => d._id === doc.id);
                            if (matchedDoc) {
                                apiDocs.push({
                                    ...matchedDoc,
                                    send_to_users: doc.send_to_users || tenantIds.map(id => ({ id })),
                                    signature_required_from: doc.signature_required_from || tenantIds.map(id => ({ id })),
                                    signatures: doc.signatures || createTenantSignatures(doc.signature_required_from || tenantIds.map(id => ({ id })))
                                });
                            } else {
                                apiDocs.push({
                                    _id: doc.id,
                                    id: doc.id,
                                    name: doc.name,
                                    urls: doc.urls || [],
                                    fileType: doc.urls?.[0]?.file_url?.split('.').pop(),
                                    send_to_users: doc.send_to_users || tenantIds.map(id => ({ id })),
                                    signature_required_from: doc.signature_required_from || tenantIds.map(id => ({ id })),
                                    signatures: doc.signatures || createTenantSignatures(doc.signature_required_from || tenantIds.map(id => ({ id })))
                                });
                            }
                        }
                    });

                    // Re-merge the property's default upload-requests that were
                    // previously removed, so they reappear as excluded and can be
                    // re-included (instead of being lost permanently).
                    const savedCustomNames = new Set(customDocs.map(d => d.name));
                    (propertyResp.data.data?.documents_to_request || []).forEach(docRequest => {
                        if (!savedCustomNames.has(docRequest)) {
                            customDocs.push({
                                id: generateObjectId(),
                                name: docRequest,
                                included: false,
                                send_to_users: tenantIds.map(id => ({ id })),
                                signature_required_from: [],
                                signatures: createTenantSignatures([])
                            });
                        }
                    });

                    setLocalSelectedDocuments(apiDocs);
                    setLocalCustomDocuments(customDocs);
                    formik.setFieldValue('attached_documents', attachedDocs);
                    lastSyncedDocsRef.current = JSON.stringify(attachedDocs);
                } else {
                    const autoSelectedDocs = processedDocs.map(doc => ({
                        ...doc,
                        send_to_users: tenantIds.map(id => ({ id })),
                        signature_required_from: tenantIds.map(id => ({ id })),
                        signatures: createTenantSignatures(tenantIds.map(id => ({ id })))
                    }));

                    const initialCustomDocs = (propertyResp.data.data?.documents_to_request || []).map(docRequest => ({
                        id: generateObjectId(),
                        name: docRequest,
                        included: true,
                        send_to_users: tenantIds.map(id => ({ id })),
                        signature_required_from: [],
                        signatures: createTenantSignatures([])
                    }));

                    setLocalSelectedDocuments(autoSelectedDocs);
                    setLocalCustomDocuments(initialCustomDocs);

                    const initialDocs = [
                        ...autoSelectedDocs.map(doc => ({
                            id: doc._id,
                            name: doc.name,
                            urls: doc.urls,
                            send_to_users: doc.send_to_users,
                            signature_required_from: doc.signature_required_from,
                            signatures: doc.signatures
                        })),
                        ...initialCustomDocs.map(doc => ({
                            id: doc.id,
                            name: doc.name,
                            urls: [{ file_url: "upload_requested", uploaded_by: 'Tenant Upload Required', uploaded_at: new Date() }],
                            send_to_users: doc.send_to_users,
                            signature_required_from: doc.signature_required_from,
                            signatures: doc.signatures
                        }))
                    ];
                    formik.setFieldValue('attached_documents', initialDocs);
                    lastSyncedDocsRef.current = JSON.stringify(initialDocs);
                }

                hasInitializedRef.current = true;
            } catch (err) {
                console.error('Error initializing data:', err);
                setError('Failed to initialize form data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        initializeData();
    }, [property_id, unit_no, building_name, tenantIds, formik, createTenantSignatures]);

    // Debounced sync with Formik
    useEffect(() => {
        if (!formik || !hasInitializedRef.current) return;

        if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);

        updateTimeoutRef.current = setTimeout(() => {
            const allDocs = [
                ...localSelectedDocuments.map(doc => ({
                    id: doc._id,
                    name: doc.name,
                    urls: doc.urls,
                    send_to_users: doc.send_to_users,
                    signature_required_from: doc.signature_required_from,
                    signatures: doc.signatures
                })),
                ...localCustomDocuments.filter(doc => doc.included !== false).map(doc => ({
                    id: doc.id,
                    name: doc.name,
                    urls: [{ file_url: "upload_requested", uploaded_by: 'Tenant Upload Required', uploaded_at: new Date() }],
                    send_to_users: doc.send_to_users,
                    signature_required_from: doc.signature_required_from,
                    signatures: doc.signatures
                }))
            ];

            const currentDocsJson = JSON.stringify(allDocs);
            if (lastSyncedDocsRef.current !== currentDocsJson) {
                formik.setFieldValue('attached_documents', allDocs);
                lastSyncedDocsRef.current = currentDocsJson;
            }
        }, 500);

        return () => {
            if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
        };
    }, [localSelectedDocuments, localCustomDocuments, formik]);

    // Toggle send to user for selected documents
    const handleToggleSendToUser = useCallback((docId, tenantId) => {
        setLocalSelectedDocuments(prev =>
            prev.map(doc => {
                if (doc._id === docId) {
                    const currentSendTo = doc.send_to_users || [];
                    const isCurrentlySent = currentSendTo.some(user => user.id === tenantId);
                    const updatedSendTo = isCurrentlySent
                        ? currentSendTo.filter(user => user.id !== tenantId)
                        : [...currentSendTo, { id: tenantId }];
                    const updatedSignatureRequired = doc.signature_required_from?.filter(user => updatedSendTo.some(u => u.id === user.id)) || [];
                    return {
                        ...doc,
                        send_to_users: updatedSendTo,
                        signature_required_from: updatedSignatureRequired,
                        signatures: createTenantSignatures(updatedSignatureRequired)
                    };
                }
                return doc;
            })
        );
    }, [createTenantSignatures]);

    // Toggle signature required for selected documents
    const handleToggleSignatureRequired = useCallback((docId, tenantId) => {
        setLocalSelectedDocuments(prev =>
            prev.map(doc => {
                if (doc._id === docId) {
                    const currentSignatureRequired = doc.signature_required_from || [];
                    const isCurrentlyRequired = currentSignatureRequired.some(user => user.id === tenantId);
                    if (!isCurrentlyRequired && !doc.send_to_users.some(user => user.id === tenantId)) {
                        return doc;
                    }
                    const updatedSignatureRequired = isCurrentlyRequired
                        ? currentSignatureRequired.filter(user => user.id !== tenantId)
                        : [...currentSignatureRequired, { id: tenantId }];
                    return {
                        ...doc,
                        signature_required_from: updatedSignatureRequired,
                        signatures: createTenantSignatures(updatedSignatureRequired)
                    };
                }
                return doc;
            })
        );
    }, [createTenantSignatures]);

    // Toggle send to user for custom documents
    const handleToggleCustomDocSendToUser = useCallback((docId, tenantId) => {
        setLocalCustomDocuments(prev =>
            prev.map(doc => {
                if (doc.id === docId) {
                    const currentSendTo = doc.send_to_users || [];
                    const isCurrentlySent = currentSendTo.some(user => user.id === tenantId);
                    const updatedSendTo = isCurrentlySent
                        ? currentSendTo.filter(user => user.id !== tenantId)
                        : [...currentSendTo, { id: tenantId }];
                    const updatedSignatureRequired = doc.signature_required_from?.filter(user => updatedSendTo.some(u => u.id === user.id)) || [];
                    return {
                        ...doc,
                        send_to_users: updatedSendTo,
                        signature_required_from: updatedSignatureRequired,
                        signatures: createTenantSignatures(updatedSignatureRequired)
                    };
                }
                return doc;
            })
        );
    }, [createTenantSignatures]);

    // Toggle signature required for custom documents
    const handleToggleCustomDocSignatureRequired = useCallback((docId, tenantId) => {
        setLocalCustomDocuments(prev =>
            prev.map(doc => {
                if (doc.id === docId) {
                    const currentSignatureRequired = doc.signature_required_from || [];
                    const isCurrentlyRequired = currentSignatureRequired.some(user => user.id === tenantId);
                    if (!isCurrentlyRequired && !doc.send_to_users.some(user => user.id === tenantId)) {
                        return doc;
                    }
                    const updatedSignatureRequired = isCurrentlyRequired
                        ? currentSignatureRequired.filter(user => user.id !== tenantId)
                        : [...currentSignatureRequired, { id: tenantId }];
                    return {
                        ...doc,
                        signature_required_from: updatedSignatureRequired,
                        signatures: createTenantSignatures(updatedSignatureRequired)
                    };
                }
                return doc;
            })
        );
    }, [createTenantSignatures]);

    // Preview handling
    const fetchSignedUrl = useCallback(async (document) => {
        if (document.signedUrl && document.signedUrlExpiry && new Date(document.signedUrlExpiry) > new Date()) {
            return { signedUrl: document.signedUrl, originalUrl: document.urls[0].file_url };
        }
        const key = extractKeyFromUrl(document.urls[0].file_url);
        if (!key) throw new Error("Invalid document URL");
        const result = await getSignedUrl({ key });
        return { signedUrl: result.signedUrl, originalUrl: document.urls[0].file_url };
    }, []);

    const handlePreviewDocument = useCallback(async (document) => {
        try {
            setPreviewLoading(true);
            setError(null);
            const urlData = await fetchSignedUrl(document);
            setLocalSelectedDocuments(prev =>
                prev.map(doc =>
                    doc._id === document._id ? { ...doc, signedUrl: urlData.signedUrl, signedUrlExpiry: new Date(Date.now() + 3600000).toISOString() } : doc
                )
            );
            setPreviewDocument({ ...document, ...urlData });
            setPreviewOpen(true);
        } catch (err) {
            setError(err.message || "Failed to fetch document preview. Please try again.");
        } finally {
            setPreviewLoading(false);
        }
    }, [fetchSignedUrl]);

    const handleClosePreview = useCallback(() => {
        setPreviewOpen(false);
        setPreviewDocument(null);
        setError(null);
    }, []);

    // Exclude / re-include a custom upload document. We keep it in the list (just
    // toggle `included`) so a removed upload-request can always be re-added.
    const handleSetCustomDocIncluded = useCallback((docId, included) => {
        setLocalCustomDocuments(prev =>
            prev.map(doc => (doc.id === docId ? { ...doc, included } : doc))
        );
    }, []);

    // Get file color
    const getFileColor = useCallback((document) => {
        const type = document.fileType?.toLowerCase() || document.urls?.[0]?.file_url?.split('.').pop()?.toLowerCase();
        switch (type) {
            case 'pdf': return 'error.main';
            case 'docx':
            case 'doc': return 'primary.main';
            case 'xlsx':
            case 'xls': return 'success.main';
            default: return 'text.secondary';
        }
    }, []);

    // Check if document is sent to tenant
    const isDocumentSentToTenant = useCallback((document, tenantId) => {
        return document.send_to_users?.some(u => u.id === tenantId) || false;
    }, []);

    // Check if signature is required from tenant
    const isSignatureRequiredFromTenant = useCallback((document, tenantId) => {
        return document.signature_required_from?.some(u => u.id === tenantId) || false;
    }, []);

    // Toggle document inclusion
    const handleToggleDocumentInclusion = useCallback((document, isSelected) => {
        if (isSelected) {
            setLocalSelectedDocuments(prev => prev.filter(doc => doc._id !== document._id));
        } else {
            const newDoc = {
                ...document,
                send_to_users: tenantIds.map(id => ({ id })),
                signature_required_from: tenantIds.map(id => ({ id })),
                signatures: createTenantSignatures(tenantIds.map(id => ({ id }))),
            };
            setLocalSelectedDocuments(prev => [...prev, newDoc]);
        }
    }, [tenantIds, createTenantSignatures]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>Loading available documents...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ my: 2 }}>
                {error}
            </Alert>
        );
    }

    const tenants = formik?.values?.tenants || [];

    return (
        <Box sx={{ p: 2 }}>
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                    <Typography className="lease-header" gutterBottom>
                        1.) Move-In Documents
                    </Typography>
                    <Typography className="lease-description" paragraph>
                        Configure documents for this lease. You can preview existing documents and configure signature requirements per tenant.
                    </Typography>

                    {documents.length === 0 ? (
                        <Alert severity="info" sx={{ my: 2 }}>
                            No pre-existing documents are available for this property.
                        </Alert>
                    ) : (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {documents.map((document) => {
                                const isSelected = localSelectedDocuments.some(doc => doc._id === document._id);
                                const selectedDoc = localSelectedDocuments.find(doc => doc._id === document._id) || document;

                                return (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={document._id}>
                                        <Card
                                            variant="outlined"
                                            className="document-card"
                                            sx={{ borderLeft: `4px solid ${getFileColor(document)}` }}
                                        >
                                            <CardContent className="document-card__content">
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    className="document-card__header"
                                                >
                                                    <Box display="flex" alignItems="center" sx={{ overflow: 'hidden' }}>
                                                        <DocumentIcon
                                                            className="document-card__icon"
                                                            sx={{ color: getFileColor(document), mr: 1 }}
                                                        />
                                                        <Typography
                                                            variant="body1"
                                                            className="document-card__title"
                                                            title={document.name}
                                                            noWrap
                                                        >
                                                            {document.name}
                                                        </Typography>
                                                    </Box>
                                                    <Tooltip title="Preview Document">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handlePreviewDocument(document)}
                                                            className="document-card__action-button document-card__header-icon"
                                                            disabled={previewLoading}
                                                        >
                                                            {previewLoading ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <PreviewIcon fontSize="inherit" />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>

                                                <Divider className="document-card__divider" sx={{ my: 1 }} />

                                                <Box className="document-card__checkbox-group">
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={() => handleToggleDocumentInclusion(document, isSelected)}
                                                                size="small"
                                                                className={`document-card__checkbox ${isSelected ? 'document-card__checkbox--checked' : ''}`}
                                                            />
                                                        }
                                                        label={<Typography className="document-card__caption" variant="caption">Include Document</Typography>}
                                                        className="document-card__checkbox-label"
                                                    />
                                                    {isSelected && (
                                                        <Box sx={{ mt: 1 }}>
                                                            {tenants.map((tenant) => (
                                                                <Box key={tenant.id} sx={{ mb: 1 }}>
                                                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                                                        {tenant.name}:
                                                                    </Typography>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={isDocumentSentToTenant(selectedDoc, tenant.id)}
                                                                                onChange={() => handleToggleSendToUser(document._id, tenant.id)}
                                                                                size="small"
                                                                                className="document-card__checkbox"
                                                                            />
                                                                        }
                                                                        label={<Typography className="document-card__caption" variant="caption">Send to tenant</Typography>}
                                                                        className="document-card__checkbox-label"
                                                                    />
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={isSignatureRequiredFromTenant(selectedDoc, tenant.id)}
                                                                                onChange={() => handleToggleSignatureRequired(document._id, tenant.id)}
                                                                                size="small"
                                                                                className="document-card__checkbox"
                                                                                disabled={!isDocumentSentToTenant(selectedDoc, tenant.id)}
                                                                            />
                                                                        }
                                                                        label={<Typography className="document-card__caption" variant="caption">Signature required</Typography>}
                                                                        className="document-card__checkbox-label"
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </CardContent>
            </Card>

            <Divider sx={{ my: 3 }} />

            <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                    <Typography className="lease-header" gutterBottom>
                        2.) Documents for Tenant to Upload
                    </Typography>
                    <Typography className="lease-description" paragraph>
                        Configure documents that tenants need to upload. Default documents from property settings are automatically included.
                    </Typography>

                    {localCustomDocuments.length === 0 ? (
                        <Alert severity="info" sx={{ my: 2 }}>
                            No document upload requests defined.
                        </Alert>
                    ) : (
                        <Grid container spacing={2}>
                            {localCustomDocuments.map((doc) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                                    <Card
                                        variant="outlined"
                                        className="document-card"
                                        sx={{
                                            borderLeft: '4px solid',
                                            borderColor: doc.included === false ? 'grey.400' : 'secondary.main',
                                            opacity: doc.included === false ? 0.6 : 1,
                                        }}
                                    >
                                        <CardContent className="document-card__content">
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                className="document-card__header"
                                            >
                                                <Box display="flex" alignItems="center" sx={{ overflow: 'hidden' }}>
                                                    <UploadIcon
                                                        className="document-card__icon"
                                                        sx={{ color: 'secondary.main', mr: 1 }}
                                                    />
                                                    <Typography
                                                        variant="body1"
                                                        className="document-card__title"
                                                        title={doc.name}
                                                        noWrap
                                                    >
                                                        {doc.name}
                                                    </Typography>
                                                </Box>
                                                {doc.included === false ? (
                                                    <Tooltip title="Add this document back to the upload list">
                                                        <Button
                                                            size="small"
                                                            color="secondary"
                                                            onClick={() => handleSetCustomDocIncluded(doc.id, true)}
                                                        >
                                                            Include
                                                        </Button>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Remove from upload list">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleSetCustomDocIncluded(doc.id, false)}
                                                            className="document-card__action-button document-card__header-icon"
                                                        >
                                                            <DeleteIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>

                                            <Divider className="document-card__divider" sx={{ my: 1 }} />

                                            {doc.included === false ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    Excluded — this won't be sent. Click “Include” to re-add it.
                                                </Typography>
                                            ) : (
                                            <Box className="document-card__checkbox-group">
                                                {tenants.map((tenant) => (
                                                    <Box key={tenant.id} sx={{ mb: 1 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                                            {tenant.name}:
                                                        </Typography>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={isDocumentSentToTenant(doc, tenant.id)}
                                                                    onChange={() => handleToggleCustomDocSendToUser(doc.id, tenant.id)}
                                                                    size="small"
                                                                    className="document-card__checkbox"
                                                                />
                                                            }
                                                            label={<Typography className="document-card__caption" variant="caption">Send to tenant</Typography>}
                                                            className="document-card__checkbox-label"
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={isSignatureRequiredFromTenant(doc, tenant.id)}
                                                                    onChange={() => handleToggleCustomDocSignatureRequired(doc.id, tenant.id)}
                                                                    size="small"
                                                                    className="document-card__checkbox"
                                                                    disabled={!isDocumentSentToTenant(doc, tenant.id)}
                                                                />
                                                            }
                                                            label={<Typography className="document-card__caption" variant="caption">Signature required</Typography>}
                                                            className="document-card__checkbox-label"
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </CardContent>
            </Card>

            <Dialog
                open={previewOpen}
                onClose={handleClosePreview}
                maxWidth="md"
                fullWidth
                disableEscapeKeyDown={false}
                keepMounted={true}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {previewDocument?.name || 'Document Preview'}
                    </Typography>
                    <IconButton onClick={handleClosePreview} size="small">
                        <HidePreviewIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {previewDocument ? (
                        <Box sx={{ height: '90vh', position: 'relative' }}>
                            {previewLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <CircularProgress />
                                    <Typography variant="body1" sx={{ ml: 2 }}>
                                        Loading document preview...
                                    </Typography>
                                </Box>
                            ) : error ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Typography color="error">{error}</Typography>
                                </Box>
                            ) : (
                                <Previewer
                                    url={previewDocument.originalUrl}
                                    signedUrl={previewDocument.signedUrl}
                                    height="90vh"
                                />
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90vh' }}>
                            <Typography>No document selected</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export { FetchAvailableForms };